import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

import { FormControl, InputLabel, Select, MenuItem, Tooltip, ClickAwayListener, Grid, Typography, Autocomplete, Avatar, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { addJiraCommentForTask, getTransitions, updateJiraIssueStatus, getJiraUser } from '../../services/jira.service';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import "./taskComponents.scss"
import { dropDownMenuProps } from '../../utils/components';
import { addOpenProjectComment, getOpenProjectIssueStatuses, updateOpenProjectTask } from '../../services/openProject.service';
import { openProjectIssueStatuses } from '../../utils/issues';
import InfoIcon from '@mui/icons-material/Info';
import { JIRA_TASK_IN_REVIEW_STATUS_ID, JIRA_TASK_IN_PROGRESS_STATUS_ID, JIRA_TASK_BLOCKED_STATUS_ID } from '../../services/const';
import { debounce } from 'lodash';
import moment from 'moment';
import { IJiraUserDetails, IUpdateJiraIssueStatusParam } from '../../common/interface';

interface DialogProps {
  isOpen: boolean;
  onClose: (isRefetch?: boolean) => void;
}

const updateStatusInfoTooltipMessage = (
  <div>
    <span>Here you can update the status of the selected task.</span><br />
    <span>Select the appropriate status and provide any necessary comments.</span>
  </div>
);



const UpdateTaskStatusDialog: React.FC<DialogProps> = ({ isOpen, onClose }) => {

  const issueDetails = useSelector((state: RootState) => state.issue);
  const projectState = useSelector((state: RootState) => state.project);
  const projectTypeMenu = projectState.projectTypeMenu;
  const projectType = projectState.projectType;
  const [openProjectStatusList, setOpenProjectStatusList] = useState<any[]>()
  const [taskTransitionList, setTaskTransitionList] = useState<any[]>([]);
  const userState = useSelector((state: RootState) => state.user);
  const userDetails = userState?.user;
  const [isInfoToolTipOpen, setIsInfoToolTipOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showMandatoryFields, setShowMandatoryFields] = useState(false);
  const [assigneeList, setAssigneeList] = useState<IJiraUserDetails[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState("");

  const getCurrentDate = useCallback(() => moment().format('YYYY-MM-DD'), []);

  const getSelectedTransition = useCallback((statusId: string) => {
    return taskTransitionList.find(t => t.id === statusId);
  }, [taskTransitionList]);

  const handleClose = () => {
    formik.resetForm();
    setShowMandatoryFields(false);
    setSelectedAssigneeId("");
    onClose()
  }

  const initialValues = {
    status: '',
    command: '',
    assignee: '',
    dueDate: getCurrentDate(),
    timeEstimation: {
      days: '',
      hours: '',
      minutes: ''
    },
    customfield_11177: '',
    linkWith: ''
  };



  const typesStatuses: Record<any, string[]> = {
    "Milestone": ["New", "To be scheduled", "In progress", "Closed", "On hold", "Rejected"],
    "Phase": ["New", "To be scheduled", "Scheduled", "In progress", "Closed", "On hold", "Rejected"],
    "Task List": ["New", "In progress", "Closed", "On hold", "Rejected"],
    "Task": ["New", "In progress", "Closed", "On hold", "Rejected"],
    "Sub Task": ["New", "In progress", "Closed", "On hold", "Rejected"],
    "Feature": ["New", "In specification", "Specified", "In progress", "Developed", "In testing", "Tested", "Test failed", "Closed", "On hold", "Rejected"],
    "Epic": ["New", "In specification", "Specified", "In progress", "Developed", "In testing", "Tested", "Test failed", "Closed", "On hold", "Rejected"],
    "User story": ["New", "In specification", "Specified", "In progress", "Developed", "In testing", "Tested", "Test failed", "Closed", "On hold", "Rejected"],
    "Bug": ["New", "Confirmed", "In progress", "Developed", "In testing", "Tested", "Test failed", "Closed", "On hold", "Rejected"],
  }


  const validationSchema = useMemo(() => {
    const hasExistingAssignee = !!issueDetails?.issueData?.fields?.assignee?.accountId;
    const hasExistingDueDate = !!issueDetails?.dueDate;
    const hasExistingEstimation = !!issueDetails?.issueData?.fields?.timetracking?.originalEstimate;

    return Yup.object({
      status: Yup.string().required('status is required'),
      command: Yup.string(),
      assignee: Yup.string().when('status', {
        is: (value: string) => {
          const transition = getSelectedTransition(value);
          return (transition?.to?.id === JIRA_TASK_IN_PROGRESS_STATUS_ID) && showMandatoryFields && !hasExistingAssignee;
        },
        then: (schema) => schema.required('Assignee is required'),
        otherwise: (schema) => schema
      }),
      dueDate: Yup.string().when('status', {
        is: (value: string) => {
          const transition = getSelectedTransition(value);
          return (transition?.to?.id === JIRA_TASK_IN_PROGRESS_STATUS_ID) && showMandatoryFields && !hasExistingDueDate;
        },
        then: (schema) => schema.required('Due date is required'),
        otherwise: (schema) => schema
      }),
      timeEstimation: Yup.object().shape({
        days: Yup.string(),
        hours: Yup.string(),
        minutes: Yup.string()
      }).when('status', {
        is: (value: string) => {
          const transition = getSelectedTransition(value);
          return (transition?.to?.id === JIRA_TASK_IN_PROGRESS_STATUS_ID) && showMandatoryFields && !hasExistingEstimation;
        },
        then: (schema) => schema.test(
          'at-least-one-field',
          'At least one time estimation field is required',
          function (value: any) {
            return !!(value?.days || value?.hours || value?.minutes);
          }
        ),
        otherwise: (schema) => schema
      }),
      customfield_11177: Yup.string().when('status', {
        is: (value: string) => {
          const transition = getSelectedTransition(value);
          return transition?.to?.id === JIRA_TASK_IN_REVIEW_STATUS_ID;
        },
        then: (schema) => schema.required('Output URL is required').url('Must be a valid URL'),
        otherwise: (schema) => schema
      }),
      linkWith: Yup.string().when('status', {
        is: (value: string) => {
          const transition = getSelectedTransition(value);
          return transition?.to?.id === JIRA_TASK_BLOCKED_STATUS_ID;
        },
        then: (schema) => schema.required('Link with is required').matches(/^[A-Z]+-\d+$/, 'Must be in format ALW-12345'),
        otherwise: (schema) => schema
      })
    });
  }, [showMandatoryFields, issueDetails, getSelectedTransition]);

  const updateOpenProjectTaskStatusWithInfo = async (values: typeof initialValues) => {
    const link = issueDetails?.workPackagesLink;
    const lockVersion = issueDetails?.issueData?.lockVersion;
    const statusLink = taskTransitionList?.find(taskType => taskType?.id === values?.status);

    const issueStatusPayload = {
      link,
      lockVersion,
      _links: {
        status: {
          href: statusLink?._links?.self?.href
        }
      }
    };

    try {
      let response = await updateOpenProjectTask(issueStatusPayload);
      if (response?.statusCode === 200) {
        toast.success('Task Status Updated successfully');
        const commentPayload = {
          id: issueDetails?.id,
          comment: {
            raw: values?.command
          }
        };
        if (values?.command) {
          let commentRes = await addOpenProjectComment(commentPayload);
          if (commentRes?.statusCode === 200) {
            toast.success('Comment added successfully');
          } else {
            toast.error('Failed to add Comment');
          }
        };
        formik.resetForm();
        onClose(true);
      } else {
        toast.error('Task Status Updation Failed');
      }
    } catch (error) {
      toast.error('Task Status Updation Failed');
    }
  }

  const updateJiraTaskStatusWithInfo = async (values: typeof initialValues) => {
    const issueStatusPayload: IUpdateJiraIssueStatusParam = {
      issueKey: issueDetails?.key,
      transition: {
        id: values.status
      }
    };

    const selectedTransition = getSelectedTransition(values.status);

    if (selectedTransition?.to?.id === JIRA_TASK_IN_PROGRESS_STATUS_ID && showMandatoryFields) {
      const hasExistingAssignee = !!issueDetails?.issueData?.fields?.assignee?.accountId;
      const hasExistingDueDate = !!issueDetails?.dueDate;
      const hasExistingEstimation = !!issueDetails?.issueData?.fields?.timetracking?.originalEstimate;

      if (!hasExistingAssignee || !hasExistingDueDate || !hasExistingEstimation) {
        issueStatusPayload.fields = {};

        if (!hasExistingAssignee && selectedAssigneeId) {
          issueStatusPayload.fields.assignee = {
            accountId: selectedAssigneeId
          };
        }

        if (!hasExistingDueDate && values.dueDate) {
          issueStatusPayload.fields.duedate = values.dueDate;
        }

        if (!hasExistingEstimation && (values.timeEstimation.days || values.timeEstimation.hours || values.timeEstimation.minutes)) {
          issueStatusPayload.fields.estimationTime = {
            days: values.timeEstimation.days || '',
            hours: values.timeEstimation.hours || '',
            minutes: values.timeEstimation.minutes || ''
          };
        }
      }
    }

    if (selectedTransition?.to?.id === JIRA_TASK_IN_REVIEW_STATUS_ID && values.customfield_11177) {
      if (!issueStatusPayload.fields) {
        issueStatusPayload.fields = {};
      }
      issueStatusPayload.fields.customfield_11177 = values.customfield_11177;
    }

    if (selectedTransition?.to?.id === JIRA_TASK_BLOCKED_STATUS_ID && values.linkWith) {
      issueStatusPayload.blockedBy = {
        id: values.linkWith
      };
    }

    try {
      let response = await updateJiraIssueStatus(issueStatusPayload)
      if (response?.statusCode === 200) {
        toast.success('Task Status Updated successfully');
        const commentPayload = {
          issueKey: issueDetails?.key,
          body: values?.command
        };
        if (values?.command) {
          let commentRes = await addJiraCommentForTask(commentPayload);
          if (commentRes?.statusCode === 200) {
            toast.success('Comment added successfully');
          } else {
            toast.error('Failed to add Comment');
          }
        }
        formik.resetForm();
        setShowMandatoryFields(false);
        setSelectedAssigneeId("");
        onClose(true);
      } else {
        toast.error('Task Status Updation Failed');
      }
    } catch (error) {
      toast.error('Task Status Updation Failed');
    }
  }

  const getUsers = async (searchString?: string) => {
    setUserLoading(true);
    try {
      const { data: fetchedUsers } = (await getJiraUser({ text: searchString || "" })) ?? {};
      setAssigneeList(fetchedUsers || []);
    } catch (error) {
      setAssigneeList([]);
    } finally {
      setUserLoading(false);
    }
  };

  const debouncedUserSearch = useMemo(
    () => debounce((searchString?: string) => getUsers(searchString), 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUserSearch.cancel();
    };
  }, [debouncedUserSearch]);

  const onSubmit = async (values: typeof initialValues) => {
    setLoading(true);
    try {
      if (projectType === projectTypeMenu[0]) {
        await updateOpenProjectTaskStatusWithInfo(values);
      } else if (projectType === projectTypeMenu[2]) {
        await updateJiraTaskStatusWithInfo(values);
      } else {
        formik.resetForm();
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const checkMandatoryFieldsRequired = (statusId: string) => {
    const selectedTransition = getSelectedTransition(statusId);
    const isInProgressStatus = selectedTransition?.to?.id === JIRA_TASK_IN_PROGRESS_STATUS_ID;

    if (isInProgressStatus && projectType === projectTypeMenu[2]) {
      const hasAssignee = !!issueDetails?.issueData?.fields?.assignee?.accountId;
      const hasDueDate = !!issueDetails?.dueDate;
      const hasEstimation = !!issueDetails?.issueData?.fields?.timetracking?.originalEstimate;

      if (!hasAssignee || !hasDueDate || !hasEstimation) {
        setShowMandatoryFields(true);

        if (!hasAssignee) {
          getUsers();
        }

        if (hasAssignee && issueDetails?.issueData?.fields?.assignee) {
          setSelectedAssigneeId(issueDetails.issueData.fields.assignee.accountId);
          formik.setFieldValue('assignee', issueDetails.issueData.fields.assignee.displayName);
        }
        if (hasDueDate && issueDetails?.dueDate) {
          formik.setFieldValue('dueDate', issueDetails.dueDate);
        }
      } else {
        setShowMandatoryFields(false);
      }
    } else {
      setShowMandatoryFields(false);
    }
  };

  const getOpenProjectIssueStatusList = async () => {
    try {
      const response = await getOpenProjectIssueStatuses();
      const openProjectTaskStatus = await response?.data?._embedded?.elements;
      console.log("[TESTING] IssueType", issueDetails.issueType);
      const issueType = issueDetails.issueType;
      let filteredStatuses: any = [];

      if (issueType && issueType in typesStatuses) {
        const issueStatuses = typesStatuses[issueType];
        filteredStatuses = openProjectTaskStatus.filter((status: any) =>
          issueStatuses.includes(status.name)
        );
        console.log("[TESTING] Filtered Status Objects:", filteredStatuses);
        if (Array.isArray(filteredStatuses) && filteredStatuses.length) {
          setTaskTransitionList(filteredStatuses);
        } else {
          setTaskTransitionList([]);
        }
      }
    } catch (error) {
      setOpenProjectStatusList([]);
    }
  }

  const getOpenProjectIssueStatus = async () => {
    setTaskTransitionList(openProjectStatusList || []);
  }

  const getJiraIssueStatus = async () => {
    const transitions = await getTransitions(issueDetails.key);
    if (Array.isArray(transitions) && transitions.length) {
      setTaskTransitionList(transitions);
    } else {
      setTaskTransitionList([]);
    }
  };

  const getWaymoreIssueStatus = async () => {
    setTaskTransitionList([]);
  }

  const getIssueStatus = async () => {
    if (projectType === projectTypeMenu[0]) {
      await getOpenProjectIssueStatus();
    } else if (projectType === projectTypeMenu[2]) {
      await getJiraIssueStatus();
    } else {
      await getWaymoreIssueStatus();
    }
  }

  useEffect(() => {
    getOpenProjectIssueStatusList();
  }, [issueDetails]);

  useEffect(() => {
    const openProjectAccessToken = localStorage.getItem('openProjectAccessToken') || ""
    if (openProjectAccessToken) {
      getOpenProjectIssueStatusList();
    }
  }, [userDetails])

  useEffect(() => {
    if (issueDetails.key)
      getIssueStatus();
  }, [issueDetails?.key])


  useEffect(() => {
    if (formik.values.status) {
      checkMandatoryFieldsRequired(formik.values.status);

      const selectedTransition = getSelectedTransition(formik.values.status);
      const isInReviewStatus = selectedTransition?.to?.id === JIRA_TASK_IN_REVIEW_STATUS_ID;
      const isBlockedStatus = selectedTransition?.to?.id === JIRA_TASK_BLOCKED_STATUS_ID;
      const isInProgressStatus = selectedTransition?.to?.id === JIRA_TASK_IN_PROGRESS_STATUS_ID;

      if (!isInReviewStatus) {
        formik.setFieldValue('customfield_11177', '');
        formik.setFieldTouched('customfield_11177', false);
      }

      if (!isBlockedStatus) {
        formik.setFieldValue('linkWith', '');
        formik.setFieldTouched('linkWith', false);
      }

      if (!isInProgressStatus) {
        formik.setFieldValue('assignee', '');
        formik.setFieldValue('dueDate', getCurrentDate());
        formik.setFieldValue('timeEstimation.days', '');
        formik.setFieldValue('timeEstimation.hours', '');
        formik.setFieldValue('timeEstimation.minutes', '');
        setSelectedAssigneeId('');

        formik.setFieldTouched('assignee', false);
        formik.setFieldTouched('dueDate', false);
        formik.setFieldTouched('timeEstimation', false);
      }
    }
  }, [formik.values.status, getSelectedTransition]);

  return (
    <Dialog className='update-status-dialog-container' open={isOpen}>
      <DialogTitle>
        <div className='title-container'>
          <span>Update Task Status</span>

          <ClickAwayListener onClickAway={(e) => setIsInfoToolTipOpen(false)}>
            <div>
              <Tooltip
                title={updateStatusInfoTooltipMessage}
                PopperProps={{
                  disablePortal: true,
                }}
                onClose={(e) => setIsInfoToolTipOpen(false)}
                open={isInfoToolTipOpen}
                disableFocusListener
                disableHoverListener
                disableTouchListener
              >
                <Button className='info-button' onClick={(e) => setIsInfoToolTipOpen(true)}><InfoIcon /></Button>
              </Tooltip>
            </div>
          </ClickAwayListener>
        </div>
      </DialogTitle>
      <div className='update-task-status-dialog-content' >
        <DialogContent>
          <form onSubmit={formik.handleSubmit}>

            {!!issueDetails?.id && (
              <TextField
                label="Selected Task"
                value={`${issueDetails?.key || ''} ${issueDetails?.issue || ''}`}
                disabled
                fullWidth
                margin="normal"
              />
            )
            }

            <div>
              <FormControl fullWidth margin="normal">
                <InputLabel className='bg-white' id="updation-status-select-lable">Status</InputLabel>
                <Select
                  fullWidth
                  labelId='updation-status-select-lable'
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.status && Boolean(formik.errors.status)}
                  MenuProps={dropDownMenuProps}
                >
                  {taskTransitionList.map((e, itemIndex) => (
                    <MenuItem key={itemIndex + e.name} value={e.id}>{e.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {showMandatoryFields && (
                <>
                  {!issueDetails?.issueData?.fields?.assignee?.accountId && (
                    <Autocomplete
                      options={assigneeList}
                      getOptionLabel={(option: IJiraUserDetails) => option?.displayName || ''}
                      loading={userLoading}
                      value={assigneeList.find((user: IJiraUserDetails) => user.accountId === selectedAssigneeId) || null}
                      onOpen={() => {
                        if (assigneeList.length === 0) {
                          getUsers();
                        }
                      }}
                      onChange={(event, newValue: IJiraUserDetails | null) => {
                        if (newValue) {
                          setSelectedAssigneeId(newValue.accountId);
                          formik.setFieldValue('assignee', newValue.displayName);
                        } else {
                          setSelectedAssigneeId('');
                          formik.setFieldValue('assignee', '');
                        }
                      }}
                      onInputChange={(event, value) => {
                        debouncedUserSearch(value);
                      }}
                      onBlur={() => {
                        formik.setFieldTouched('assignee', true);
                      }}
                      renderOption={(props, option: IJiraUserDetails) => (
                        <li {...props} key={option.accountId}>
                          <Avatar
                            src={option?.avatarUrls?.['48x48']}
                            sx={{ width: 24, height: 24, marginRight: 1 }}
                          />
                          {option.displayName}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assignee"
                          margin="normal"
                          error={formik.touched.assignee && Boolean(formik.errors.assignee)}
                          helperText={formik.touched.assignee && formik.errors.assignee}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {userLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  )}

                  {!issueDetails?.dueDate && (
                    <TextField
                      label="Due Date"
                      type="date"
                      name="dueDate"
                      value={formik.values.dueDate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.dueDate && Boolean(formik.errors.dueDate)}
                      helperText={formik.touched.dueDate && formik.errors.dueDate}
                      fullWidth
                      margin="normal"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}

                  {!issueDetails?.issueData?.fields?.timetracking?.originalEstimate && (
                    <Grid container spacing={1} sx={{ mt: 1 }}>
                      <Grid item xs={12}>
                        <Typography variant="body2" gutterBottom>
                          Time Estimation
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth>
                          <InputLabel className='bg-white' error={formik.touched.timeEstimation && Boolean(formik.errors.timeEstimation)}>Days</InputLabel>
                          <Select
                            name="timeEstimation.days"
                            value={formik.values.timeEstimation.days}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.timeEstimation && Boolean(formik.errors.timeEstimation)}
                          >
                            <MenuItem value="">-</MenuItem>
                            <MenuItem value="1">1</MenuItem>
                            <MenuItem value="2">2</MenuItem>
                            <MenuItem value="3">3</MenuItem>
                            <MenuItem value="4">4</MenuItem>
                            <MenuItem value="5">5</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth>
                          <InputLabel className='bg-white' error={formik.touched.timeEstimation && Boolean(formik.errors.timeEstimation)}>Hours</InputLabel>
                          <Select
                            name="timeEstimation.hours"
                            value={formik.values.timeEstimation.hours}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.timeEstimation && Boolean(formik.errors.timeEstimation)}
                            MenuProps={dropDownMenuProps}
                          >
                            <MenuItem value="">-</MenuItem>
                            <MenuItem value="1">1</MenuItem>
                            <MenuItem value="2">2</MenuItem>
                            <MenuItem value="3">3</MenuItem>
                            <MenuItem value="4">4</MenuItem>
                            <MenuItem value="5">5</MenuItem>
                            <MenuItem value="6">6</MenuItem>
                            <MenuItem value="7">7</MenuItem>
                            <MenuItem value="8">8</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth>
                          <InputLabel className='bg-white' error={formik.touched.timeEstimation && Boolean(formik.errors.timeEstimation)}>Minutes</InputLabel>
                          <Select
                            name="timeEstimation.minutes"
                            value={formik.values.timeEstimation.minutes}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.timeEstimation && Boolean(formik.errors.timeEstimation)}
                          >
                            <MenuItem value="">-</MenuItem>
                            <MenuItem value="10">10</MenuItem>
                            <MenuItem value="20">20</MenuItem>
                            <MenuItem value="30">30</MenuItem>
                            <MenuItem value="40">40</MenuItem>
                            <MenuItem value="50">50</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {formik.touched.timeEstimation && formik.errors.timeEstimation && (
                        <Grid item xs={12} sx={{ pt: '3px !important' }}>
                          <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                            {formik.errors.timeEstimation as string}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </>
              )}

              {(() => {
                const selectedTransition = getSelectedTransition(formik.values.status);
                return selectedTransition?.to?.id === JIRA_TASK_IN_REVIEW_STATUS_ID;
              })() && (
                <TextField
                  label="Output"
                  type="url"
                  name="customfield_11177"
                  value={formik.values.customfield_11177}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.customfield_11177 && Boolean(formik.errors.customfield_11177)}
                  helperText={formik.touched.customfield_11177 && formik.errors.customfield_11177}
                  fullWidth
                  margin="normal"
                  placeholder="https://example.com"
                />
              )}

              {(() => {
                const selectedTransition = getSelectedTransition(formik.values.status);
                return selectedTransition?.to?.id === JIRA_TASK_BLOCKED_STATUS_ID;
              })() && (
                <TextField
                  label="Link With"
                  type="text"
                  name="linkWith"
                  value={formik.values.linkWith}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.linkWith && Boolean(formik.errors.linkWith)}
                  helperText={formik.touched.linkWith && formik.errors.linkWith}
                  fullWidth
                  margin="normal"
                  placeholder="ALW-12345"
                />
              )}

              <TextField
                multiline
                rows={3}
                label="Comment"
                type="text"
                name="command"
                value={formik.values.command}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.command && Boolean(formik.errors.command)}
                helperText={formik.touched.command && formik.errors.command}
                fullWidth
                margin="normal"
              />
            </div>
            <div className='action-button-container' style={{ display: 'flex', justifyContent: 'space-between' }} >
              <Button variant="contained" color="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button disabled={loading} type="submit" variant="contained" color="primary">
                {loading ? "Submitting" : "Submit"}
              </Button>
            </div>

          </form>
        </DialogContent>
      </div>
    </Dialog>
  );
};


export default UpdateTaskStatusDialog;
